import React, { useState, useEffect } from "react"
import LockOutlinedIcon from "@mui/icons-material/LockOutlined"
import styled from "styled-components"
import Grid from "@mui/material/Grid"
import CircularProgress from "@mui/material/CircularProgress"
import FormControlLabel from "@mui/material/FormControlLabel"
import Checkbox from "@mui/material/Checkbox"
import {
  Avatar,
  Container,
  CssBaseline,
  FormControl,
  Theme,
  Typography,
} from "@mui/material"
import { useLazyApi } from "../../hooks/useLazyApi"
import { useAuthDispatch } from "../../contexts/authContext"
import { Button } from "../ui/Button"
import { FormInput } from "../ui/Input"
import { useSignup } from "../../hooks/signupHook"
import { FormContextType, useForm } from "../../contexts/formContext"
import { klaviyoIdentify } from "../../utils/klaviyo"
import { getFreshdeskQueryParams } from "../../utils/queryParams"
import { navigate } from "gatsby"
import { Box, SxProps } from "@mui/system"

const SIGNUP_REDIRECT_URL = "/email-confirmation"

const AuthOptionsSection = styled.div`
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
`

const StyledLink = styled.a`
  color: var(--color-primary);
  font-weight: 700;
  cursor: pointer;
  text-align: right;
`

const LoadingContainer = styled.div`
  text-align: center;
  margin-top: 1rem;
`

const LoginSignupForm = ({
  showHeader = true,
  startLoginMode = true,
  redirectUrl,
  noRedirect,
  confirmationPage,
  confirmationSuccessPage,
  email = "",
  freshdesk = false,
  alreadyHaveAccountTxt,
  loginMode,
  setLoginMode,
}: {
  showHeader: boolean
  startLoginMode: boolean
  redirectUrl: string
  noRedirect: boolean
  confirmationPage: string
  confirmationSuccessPage: string
  email?: string
  freshdesk?: boolean
  alreadyHaveAccountTxt?: string
  loginMode?: boolean
  setLoginMode?: (x: boolean) => void
}) => {
  const dispatch = useAuthDispatch()

  const [isLoginMode, setIsLoginMode] = useState<boolean>(
    startLoginMode || loginMode
  )
  const [isPasswordResetMode, setIsPasswordResetMode] = useState(false)

  const [authMessage, setAuthMessage] = useState("")
  const [heading, setHeading] = useState(startLoginMode ? "Login" : "Signup")
  const [buttonTitle, setButtonTitle] = useState(
    startLoginMode ? "Login" : "Signup"
  )
  const { formState, formActions } = useForm() as FormContextType

  const [
    signup,
    signupLoading,
    signupError,
    signupData,
    freshdeskSignupLoading,
    freshdeskSignupError,
    freshdeskSignupData,
  ] = useSignup()
  const [login, { loading, error, data }] = useLazyApi("account", "login")
  const [
    freshdeskLogin,
    { loading: freshdeskLoading, error: freshdeskError, data: freshdeskData },
  ] = useLazyApi("account", "FreshdeskLogin")

  useEffect(() => {
    formActions.resetForm()
    if (email) {
      formState.inputs.email.value = email
    }
  }, [])

  const [
    requestPasswordReset,
    {
      loading: resetLoading,
      error: resetError,
      data: resetData,
      errMsg: resetErrMsg,
    },
  ] = useLazyApi("account", "requestPassRst")

  useEffect(() => {
    if (isLoginMode && !isPasswordResetMode) {
      if (data) {
        const userData = data.login
        dispatch({
          type: "LOGIN",
          payload: userData,
          ...(noRedirect ? {} : { redirectUrl }),
        })
      }

      if (freshdeskData?.FreshdeskLogin) {
        navigate(freshdeskData.FreshdeskLogin)
      }
    }
  }, [data, freshdeskData])

  useEffect(() => {
    if (isLoginMode && !isPasswordResetMode) {
      if (error) {
        setAuthMessage(error.message || "Invalid login credentials.")
      }
    }
  }, [error])

  useEffect(() => {
    if (!isLoginMode && !isPasswordResetMode) {
      if (signupData) {
        const userData = signupData.CreateUser
        dispatch({
          type: "LOGIN",
          payload: userData,
        })
        if (window && !noRedirect) {
          window.location.replace(`/${confirmationPage}` || SIGNUP_REDIRECT_URL)
        }
      }
      if (signupError) {
        setAuthMessage(signupError.message)
      }
    }
  }, [signupData, signupError])

  useEffect(() => {
    if (freshdeskSignupData?.FreshdeskSignup) {
      navigate(freshdeskSignupData.FreshdeskSignup)
    }
  }, [freshdeskSignupData])

  useEffect(() => {
    if (isPasswordResetMode) {
      if (resetError) {
        setAuthMessage(
          resetErrMsg || "Error requesting a password reset...please try again."
        )
      }
    }
  }, [resetError, resetErrMsg])

  useEffect(() => {
    if (freshdeskError) {
      setAuthMessage(freshdeskError.message)
    }
    if (freshdeskSignupError) {
      setAuthMessage(freshdeskSignupError.message)
    }
  }, [freshdeskError, freshdeskSignupError])

  useEffect(() => {
    if (setLoginMode) {
      setLoginMode(isLoginMode)
    }
  }, [isLoginMode])

  useEffect(() => {
    formActions.setSkipValidationFunction((field: string): boolean => {
      if (isPasswordResetMode) {
        return [
          "firstName",
          "lastName",
          "password",
          "passwordConfirm",
        ].includes(field)
      } else if (isLoginMode) {
        return ["firstName", "lastName", "passwordConfirm"].includes(field)
      }
      return false
    })
    if (isPasswordResetMode) {
      setHeading("Forgot Your Password?")
      setButtonTitle("Send reset email")
    } else if (isLoginMode) {
      // Switch to login mode
      setHeading("Login")
      setButtonTitle("Login")
      setIsPasswordResetMode(false)
    } else {
      // Switch to signup mode
      setHeading("Signup")
      setButtonTitle("Signup")
      setIsPasswordResetMode(false)
    }
  }, [isLoginMode, isPasswordResetMode])

  const authSubmitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setAuthMessage("")

    if (!formActions.isValid()) {
      return
    }
    if (isPasswordResetMode) {
      requestPasswordReset({
        variables: { email: formState.inputs.email.value },
      })
    } else if (isLoginMode) {
      klaviyoIdentify(formState.inputs.email.value)
      if (freshdesk) {
        const { state, nonce } = getFreshdeskQueryParams()
        freshdeskLogin({
          variables: {
            email: formState.inputs.email.value,
            password: formState.inputs.password.value,
            state,
            nonce,
          },
        })
      } else {
        login({
          variables: {
            email: formState.inputs.email.value,
            password: formState.inputs.password.value,
          },
        })
      }
    } else {
      if (!freshdesk) {
        try {
          signup({ ...formState.inputs, signupOrigin: "WEB_ACCOUNT" })
          signupData && setIsLoginMode(true)
        } catch (error) {
          setAuthMessage(error.message)
        }
      } else {
        const { state, nonce } = getFreshdeskQueryParams()
        signup(
          { ...formState.inputs, signupOrigin: "WEB_ACCOUNT", state, nonce },
          true
        )
      }
    }
  }

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Box
        sx={
          {
            marginTop: (theme: Theme) => theme.spacing(4),
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          } as SxProps
        }
      >
        {showHeader && (
          <>
            <Avatar
              sx={{
                margin: (theme: Theme) => theme.spacing(1),
                backgroundColor: "var(--color-teal)",
              }}
            >
              <LockOutlinedIcon />
            </Avatar>
            <Typography component="h1" variant="h5" data-testid={"form-title"}>
              {heading}
            </Typography>
          </>
        )}
        {!resetData && (
          <Box
            sx={
              {
                width: "100%",
                mt: (theme: Theme) => theme.spacing(3),
              } as SxProps
            }
          >
            <form
              onSubmit={authSubmitHandler}
              noValidate
              data-testid={isLoginMode ? "login-form" : "signup-form"}
            >
              <Grid container spacing={2}>
                {!isLoginMode && !isPasswordResetMode && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <FormControl variant="standard" fullWidth>
                        <FormInput
                          fieldId="user-first-name"
                          fieldName="firstName"
                          inputs={formState.inputs}
                          inputHandler={formActions.inputHandler}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl variant="standard" fullWidth>
                        <FormInput
                          fieldId="user-last-name"
                          fieldName="lastName"
                          inputs={formState.inputs}
                          inputHandler={formActions.inputHandler}
                        />
                      </FormControl>
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <FormControl variant="standard" fullWidth>
                    <FormInput
                      fieldId="user-email"
                      fieldName="email"
                      fieldType="email"
                      inputs={formState.inputs}
                      inputHandler={formActions.inputHandler}
                    />
                  </FormControl>
                </Grid>

                {!isPasswordResetMode && (
                  <Grid item xs={12}>
                    <FormControl variant="standard" fullWidth>
                      <FormInput
                        fieldId="user-password"
                        fieldName="password"
                        fieldType="password"
                        inputs={formState.inputs}
                        inputHandler={formActions.inputHandler}
                      />
                    </FormControl>
                  </Grid>
                )}
                {!isPasswordResetMode && !isLoginMode && (
                  <Grid item xs={12}>
                    <FormControl variant="standard" fullWidth>
                      <FormInput
                        fieldId="user-password-confirm"
                        fieldName="passwordConfirm"
                        fieldType="password"
                        inputs={formState.inputs}
                        inputHandler={formActions.inputHandler}
                      />
                    </FormControl>
                  </Grid>
                )}
                {!isLoginMode && !isPasswordResetMode && (
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formState.inputs.allowMarketing.value}
                          onChange={formActions.inputHandler}
                          value="allowMarketing"
                          name="allowMarketing"
                          color="primary"
                        />
                      }
                      label="I want to receive inspiration and updates via email."
                    />
                  </Grid>
                )}
              </Grid>
              {authMessage && <p className="error">{authMessage}</p>}
              <Grid container justifyContent="center" sx={{ mt: "1rem" }}>
                <Button
                  type="primary"
                  submit={true}
                  icon="key"
                  id={
                    isPasswordResetMode
                      ? "pass_reset"
                      : isLoginMode
                      ? "login"
                      : "signup"
                  }
                  sectionId="login_signup"
                  title={buttonTitle}
                  disabled={
                    data ||
                    loading ||
                    signupLoading ||
                    resetLoading ||
                    freshdeskLoading ||
                    freshdeskSignupLoading
                  }
                  testId={
                    isLoginMode ? "login-submit-button" : "signup-submit-button"
                  }
                />
              </Grid>

              {(data ||
                loading ||
                signupLoading ||
                resetLoading ||
                freshdeskLoading ||
                freshdeskSignupLoading) && (
                <LoadingContainer>
                  <CircularProgress />
                </LoadingContainer>
              )}
              <Grid container justifyContent="flex-end">
                <Grid item>
                  <AuthOptionsSection>
                    <StyledLink
                      onClick={() => {
                        setIsPasswordResetMode(false)
                        setIsLoginMode(
                          isLoginMode && !isPasswordResetMode ? false : true
                        )
                      }}
                      data-testid={"nav-signup"}
                    >
                      {isLoginMode && !isPasswordResetMode
                        ? `Signup`
                        : alreadyHaveAccountTxt ?? `Login`}
                    </StyledLink>
                    {isLoginMode && !isPasswordResetMode && (
                      <StyledLink
                        onClick={() => setIsPasswordResetMode(true)}
                        data-testid={"nav-forgot-password"}
                      >
                        Forgot your password?
                      </StyledLink>
                    )}
                  </AuthOptionsSection>
                </Grid>
              </Grid>
            </form>
          </Box>
        )}
        {resetData && (
          <Box sx={{ textAlign: "center" }}>
            <p className={`large`}>
              <em>Success, please check your email to unlock your account.</em>
            </p>
          </Box>
        )}
      </Box>
    </Container>
  )
}

export default LoginSignupForm
